import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Tooltip } from "antd";
import { all_routes } from "../../Router/all_routes";
// import { Settings, User } from "feather-icons-react/build/IconComponents";
import { getUserProfile } from "../../api/userApi";
import LanguageSwitcher from "../../InitialPage/Sidebar/LanguageSwitcher";
import { Link, useNavigate } from "react-router-dom";
import { logout } from "../../api/authApi";
import * as XLSX from "xlsx";
import { getPosOverviewReport } from "../../api/transactionReportApi";

// ---- helper: convert catalog key -> public URL ----
const toPublicUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v; // already a full URL
  const base = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/+$/, "");
  const key = String(v).replace(/^\/+/, "");
  return `${base}/${key}`;
};

const DEFAULT_REPORT_LIMIT = 100;
const DEFAULT_REPORT_TYPE = "cash";
const MAX_PDF_ORDERS = 25;

const toYmd = (dateLike) => {
  const date = new Date(dateLike);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toAmount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const toInt = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : 0;
};

const normalizeName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.en || value.ar || "";
  return String(value);
};

const triggerBlobDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

const sanitizePdfLine = (line = "") =>
  String(line)
    .replace(/[^\x20-\x7E]/g, "?")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const buildSimplePdfBlob = (lines = []) => {
  const safeLines = (lines.length ? lines : ["No data"]).map(sanitizePdfLine);
  const commands = ["BT", "/F1 10 Tf", "40 800 Td", "14 TL"];

  safeLines.forEach((line, index) => {
    commands.push(index === 0 ? `(${line}) Tj` : `T* (${line}) Tj`);
  });
  commands.push("ET");

  const content = commands.join("\n");
  const objects = {
    1: "<< /Type /Catalog /Pages 2 0 R >>",
    2: "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    3:
      "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>",
    4: "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
    5: `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
  };

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let i = 1; i <= 5; i += 1) {
    offsets[i] = pdf.length;
    pdf += `${i} 0 obj\n${objects[i]}\nendobj\n`;
  }

  const xrefStart = pdf.length;
  pdf += "xref\n0 6\n";
  pdf += "0000000000 65535 f \n";
  for (let i = 1; i <= 5; i += 1) {
    pdf += `${String(offsets[i]).padStart(10, "0")} 00000 n \n`;
  }
  pdf += "trailer\n<< /Size 6 /Root 1 0 R >>\n";
  pdf += `startxref\n${xrefStart}\n%%EOF`;

  return new Blob([pdf], { type: "application/pdf" });
};

const buildReportParams = (userId) => {
  const to = toYmd(new Date());
  const firstDayOfMonth = new Date();
  firstDayOfMonth.setDate(1);

  return {
    from: toYmd(firstDayOfMonth),
    to,
    userId: userId || undefined,
    limit: DEFAULT_REPORT_LIMIT,
    type: DEFAULT_REPORT_TYPE,
  };
};

const downloadOverviewExcel = (report, params) => {
  const summary = report?.summary || {};
  const summaryRows = [
    { Metric: "Total Transactions", Value: toInt(summary.totalTransactions) },
    { Metric: "Total Sales Amount", Value: Number(toAmount(summary.totalSalesAmount)) },
    { Metric: "Total Received Amount", Value: Number(toAmount(summary.totalReceivedAmount)) },
    { Metric: "Total Items Sold", Value: toInt(summary.totalItemsSold) },
    { Metric: "From", Value: params.from || "-" },
    { Metric: "To", Value: params.to || "-" },
    { Metric: "Payment Type", Value: params.type || "-" },
  ];

  const orderRows = (report?.orders || []).map((order, index) => {
    const sellerName = [normalizeName(order?.seller?.firstName), normalizeName(order?.seller?.lastName)]
      .join(" ")
      .trim();

    return {
      Sr: index + 1,
      OrderId: order?.orderId || "-",
      TransactionId: order?.transactionId || "-",
      Type: order?.type || "-",
      TotalAmount: Number(toAmount(order?.totalAmount)),
      ReceivedAmount: Number(toAmount(order?.receivedAmount)),
      ItemsSold: toInt(order?.itemsSold),
      Seller: sellerName || order?.seller?.username || "-",
      CreatedAt: order?.createdAt ? new Date(order.createdAt).toLocaleString() : "-",
    };
  });

  const userRows = (report?.userSales || []).map((user, index) => ({
    Sr: index + 1,
    UserId: user?.userId || "-",
    Name: [normalizeName(user?.firstName), normalizeName(user?.lastName)].join(" ").trim() || "-",
    Username: user?.username || "-",
    Role: user?.role || "-",
    TotalTransactions: toInt(user?.totalTransactions),
    TotalSalesAmount: Number(toAmount(user?.totalSalesAmount)),
    TotalItemsSold: toInt(user?.totalItemsSold),
  }));

  const productRows = (report?.productSales || []).map((product, index) => ({
    Sr: index + 1,
    ProductId: product?.productId || "-",
    Name: product?.name || "-",
    TotalQuantitySold: toInt(product?.totalQuantitySold),
    TotalSalesAmount: Number(toAmount(product?.totalSalesAmount)),
    TransactionCount: toInt(product?.transactionCount),
    SellerCount: toInt(product?.sellerCount),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), "Summary");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(orderRows), "Orders");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(userRows), "UserSales");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(productRows), "ProductSales");
  XLSX.writeFile(wb, `pos_overview_${params.from}_to_${params.to}.xlsx`);
};

const downloadOverviewPdf = (report, params) => {
  const summary = report?.summary || {};
  const lines = [
    "POS Selling Overview Report",
    `From: ${params.from || "-"}`,
    `To: ${params.to || "-"}`,
    `Type: ${params.type || "-"}`,
    "",
    "Summary",
    `Total Transactions: ${toInt(summary.totalTransactions)}`,
    `Total Sales Amount: ${toAmount(summary.totalSalesAmount)}`,
    `Total Received Amount: ${toAmount(summary.totalReceivedAmount)}`,
    `Total Items Sold: ${toInt(summary.totalItemsSold)}`,
    "",
    "Recent Orders",
  ];

  const orders = report?.orders || [];
  if (!orders.length) {
    lines.push("No orders found for this range.");
  } else {
    orders.slice(0, MAX_PDF_ORDERS).forEach((order, idx) => {
      const seller = [
        normalizeName(order?.seller?.firstName),
        normalizeName(order?.seller?.lastName),
      ]
        .join(" ")
        .trim();
      const sellerText = seller || order?.seller?.username || "-";
      const orderLabel = order?.orderId || order?.transactionId || "-";
      lines.push(
        `${idx + 1}. ${orderLabel} | ${order?.type || "-"} | Total ${toAmount(
          order?.totalAmount
        )} | Items ${toInt(order?.itemsSold)} | Seller ${sellerText}`
      );
    });
  }

  const pdfBlob = buildSimplePdfBlob(lines);
  triggerBlobDownload(pdfBlob, `pos_overview_${params.from}_to_${params.to}.pdf`);
};

const PosHeader = () => {
  const route = all_routes;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [profileImage, setProfileImage] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userId, setUserId] = useState("");
  const [reportLoading, setReportLoading] = useState("");

  const navigate = useNavigate();

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate("/pos/signin", { replace: true });
  };

  const toggleFullscreen = (elem) => {
    elem = elem || document.documentElement;
    if (
      !document.fullscreenElement &&
      !document.mozFullScreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      const result = await getUserProfile();
      if (result.success) {
        const raw =
          result.data.profileImageUrl || result.data.profileImage || "";
        setProfileImage(raw ? toPublicUrl(raw) : "");
        setFirstName(result.data.firstName || "");
        setLastName(result.data.lastName || "");
        setUserId(result.data._id || result.data.id || "");
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
          document.mozFullScreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // update every second

    return () => clearInterval(interval); // cleanup on unmount
  }, []);

  const formattedTime = currentTime.toLocaleTimeString("en-US", {
    hour12: false, // set to true for AM/PM
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const handleReportDownload = async (format) => {
    if (reportLoading) return;

    setReportLoading(format);
    try {
      const params = buildReportParams(userId);
      const report = await getPosOverviewReport(params);

      if (!report?.success) {
        throw new Error(report?.message || "Failed to fetch report data.");
      }

      if (format === "excel") {
        downloadOverviewExcel(report, params);
      } else {
        downloadOverviewPdf(report, params);
      }
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to download report. Please try again."
      );
    } finally {
      setReportLoading("");
    }
  };

  return (
    <>
      {/* Header */}
      <div className="header pos-header">
        {/* Logo */}
        <div className="header-left active">
          <Link to={all_routes.newdashboard} className="logo logo-normal">
            <ImageWithBasePath src="assets/img/logo.png" alt="Img" />
          </Link>
          <Link to={all_routes.newdashboard} className="logo logo-white">
            <ImageWithBasePath src="assets/img/logo-white.png" alt="Img" />
          </Link>
          <Link to={all_routes.newdashboard} className="logo-small">
            <ImageWithBasePath src="assets/img/logo-small.png" alt="Img" />
          </Link>
        </div>
        {/* /Logo */}
        <Link id="mobile_btn" className="mobile_btn d-none" to="#sidebar">
          <span className="bar-icon">
            <span />
            <span />
            <span />
          </span>
        </Link>
        {/* Header Menu */}
        <ul className="nav user-menu">
          {/* Search */}
          <li className="nav-item time-nav">
            <span className="bg-teal text-white d-inline-flex align-items-center">
              <ImageWithBasePath
                src="assets/img/icons/clock-icon.svg"
                alt="img"
                className="me-2 ms-2"
              />
              {formattedTime}
            </span>
          </li>
          {/* /Search */}
          {/* <li className="nav-item pos-nav">
            <Link
              to={all_routes.newdashboard}
              className="btn btn-purple btn-md d-inline-flex align-items-center"
            >
              <i className="ti ti-world me-1" />
              {window.translate("Dashboard")}
            </Link>
          </li> */}
          {/* Select Store */}
          {/* <li className="nav-item dropdown has-arrow main-drop select-store-dropdown">
                        <Link
                            to="#"
                            className="dropdown-toggle nav-link select-store"
                            data-bs-toggle="dropdown"
                        >
                            <span className="user-info">
                                <span className="user-letter">
                                    <ImageWithBasePath
                                        src="assets/img/store/store-01.png"
                                        alt="Store Logo"
                                        className="img-fluid"
                                    />
                                </span>
                                <span className="user-detail">
                                    <span className="user-name">Freshmart</span>
                                </span>
                            </span>
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                            <Link to="#" className="dropdown-item">
                                <ImageWithBasePath
                                    src="assets/img/store/store-01.png"
                                    alt="Store Logo"
                                    className="img-fluid"
                                />
                                Freshmart
                            </Link>
                            <Link to="#" className="dropdown-item">
                                <ImageWithBasePath
                                    src="assets/img/store/store-02.png"
                                    alt="Store Logo"
                                    className="img-fluid"
                                />
                                Grocery Apex
                            </Link>
                            <Link to="#" className="dropdown-item">
                                <ImageWithBasePath
                                    src="assets/img/store/store-03.png"
                                    alt="Store Logo"
                                    className="img-fluid"
                                />
                                Grocery Bevy
                            </Link>
                            <Link to="#" className="dropdown-item">
                                <ImageWithBasePath
                                    src="assets/img/store/store-04.png"
                                    alt="Store Logo"
                                    className="img-fluid"
                                />
                                Grocery Eden
                            </Link>
                        </div>
                    </li> */}
          {/* /Select Store */}
          <li className="nav-item nav-item-box">
            <Link
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#calculator"
              className="bg-orange border-orange text-white"
            >
              <i className="ti ti-calculator" />
            </Link>
          </li>

          {/* Lang */}
          <li className="nav-item-box">
            <LanguageSwitcher /> {/* no <Link> wrapper */}
          </li>

          <li className="nav-item nav-item-box">
            <Tooltip title="Maximize" placement="right">
              <Link
                to="#"
                id="btnFullscreen"
                onClick={() => toggleFullscreen()}
                className={isFullscreen ? "Exit Fullscreen" : "Go Fullscreen"}
              >
                <i className="ti ti-maximize" />
              </Link>
            </Tooltip>
          </li>
          <li className="nav-item dropdown nav-item-box">
            <Tooltip title="Download Report" placement="right">
              <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                <i className="ti ti-download" />
              </Link>
            </Tooltip>
            <div className="dropdown-menu dropdown-menu-end">
              <button
                type="button"
                className="dropdown-item"
                disabled={!!reportLoading}
                onClick={() => handleReportDownload("pdf")}
              >
                <i className="ti ti-file-type-pdf me-2" />
                {reportLoading === "pdf" ? "Downloading PDF..." : "Download PDF"}
              </button>
              <button
                type="button"
                className="dropdown-item"
                disabled={!!reportLoading}
                onClick={() => handleReportDownload("excel")}
              >
                <i className="ti ti-file-spreadsheet me-2" />
                {reportLoading === "excel" ? "Downloading Excel..." : "Download Excel"}
              </button>
            </div>
          </li>
          {/* <li
                        className="nav-item nav-item-box"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-title="Cash Register"
                    >
                        <Tooltip title='Cash Register' placement='right'>
                            <Link to="#" data-bs-toggle="modal" data-bs-target="#cash-register">
                                <i className="ti ti-cash" />
                            </Link>
                        </Tooltip>

                    </li> */}
          {/* <li
            className="nav-item nav-item-box"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-title="Print Last Reciept"
          >
            <Tooltip title="Print Last Reciept" placement="right">
              <Link to="#">
                <i className="ti ti-printer" />
              </Link>
            </Tooltip>
          </li> */}
          {/* <li
                        className="nav-item nav-item-box"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-title="Today’s Sale"
                    >
                    <Tooltip title='Today&apos;s Sale' placement='right'>
                        <Link to="#" data-bs-toggle="modal" data-bs-target="#today-sale">
                            <i className="ti ti-progress" />
                        </Link>
                        </Tooltip>
                    </li> */}
          {/* <li
                        className="nav-item nav-item-box"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-title="Today’s Profit"
                    >
                    <Tooltip title='Today’s Profit' placement='right'>
                        <Link to="#" data-bs-toggle="modal" data-bs-target="#today-profit">
                            <i className="ti ti-chart-infographic" />
                        </Link>
                        </Tooltip>
                    </li> */}
          {/* <li
                        className="nav-item nav-item-box"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-title="POS Settings"
                    >
                    <Tooltip title='POS Settings' placement='bottom'>
                        <Link to={all_routes.possettings}>
                            <i className="ti ti-settings" />
                        </Link>
                        </Tooltip>
                    </li> */}
          <li className="nav-item dropdown has-arrow main-drop profile-nav">
            <a
              href="#"
              className="nav-link userset"
              data-bs-toggle="dropdown"
              aria-expanded="true"
            >
              <span className="user-info p-0">
                <span className="user-letter">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="img-fluid"
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <ImageWithBasePath
                      src="assets/img/profiles/avator1.jpg"
                      alt="Default Avatar"
                      className="img-fluid"
                    />
                  )}
                </span>
              </span>
            </a>
            <div className="dropdown-menu menu-drop-user">
              <div className="profileset d-flex align-items-center">
                <span className="user-img me-2">
                  <span className="user-letter">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        className="img-fluid"
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <ImageWithBasePath
                        src="assets/img/profiles/avator1.jpg"
                        alt="Default Avatar"
                        className="img-fluid"
                      />
                    )}
                  </span>
                </span>
                <div>
                  <h6 className="fw-medium">
                    {" "}
                    {firstName} {lastName}
                  </h6>
                  {/* <p>Admin</p> */}
                </div>
              </div>
              <Link className="dropdown-item" to={route.profile}>
                <i className="ti ti-user-circle me-2" />
                {window.translate("MyProfile")}
              </Link>
              {/* <Link className="dropdown-item" to={route.salesreport}>
                  <i className="ti ti-file-text me-2" />
                  Reports
                </Link> */}
              {/* <Link className="dropdown-item" to={route.generalsettings}>
                  <i className="ti ti-settings-2 me-2" />
                  Settings
                </Link> */}
              <hr className="my-2" />
              <Link
                className="dropdown-item logout pb-0"
                to="#"
                onClick={handleLogout}
              >
                <i className="ti ti-logout me-2" />
                {window.translate("Logout")}
              </Link>
            </div>
          </li>
        </ul>
        {/* /Header Menu */}
        {/* Mobile Menu */}
        <div className="dropdown mobile-user-menu">
          <Link
            to="#"
            className="nav-link dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-v" />
          </Link>

          <div className="dropdown-menu dropdown-menu-right">
            <Link className="dropdown-item" to="profile">
              {window.translate("MyProfile")}
            </Link>

            {/* <Link
              to={all_routes.newdashboard}
              className="btn btn-md d-inline-flex align-items-center"
            >
              <i className="ti ti-world me-1" />
              {window.translate("Dashboard")}
            </Link> */}

            <button
              type="button"
              className="dropdown-item"
              disabled={!!reportLoading}
              onClick={() => handleReportDownload("pdf")}
            >
              {reportLoading === "pdf" ? "Downloading PDF..." : "Download PDF"}
            </button>
            <button
              type="button"
              className="dropdown-item"
              disabled={!!reportLoading}
              onClick={() => handleReportDownload("excel")}
            >
              {reportLoading === "excel" ? "Downloading Excel..." : "Download Excel"}
            </button>
            <Link className="dropdown-item" to="#" onClick={handleLogout}>
              {window.translate("Logout")}
            </Link>
            <Link className="dropdown-item mb-2">
              <LanguageSwitcher /> {/* no <Link> wrapper */}
            </Link>
          </div>
        </div>
        {/* /Mobile Menu */}
      </div>
      {/* Header */}
    </>
  );
};

export default PosHeader;
